"use client";

import { toast } from "sonner";
import { createClientLogger } from "@/lib/logging/client";
import {
  beginNativePaymentNavigation,
  endNativePaymentNavigation,
  isNativePaymentShell,
} from "@/lib/native/payment-navigation";
import {
  openRazorpayCheckout,
  type RazorpayCheckoutOptions,
  type RazorpaySuccessResponse,
} from "@/lib/razorpay-checkout";

const logger = createClientLogger(
  "features/payments/lib/razorpay-payment-flow",
);

type ActionResult<T> =
  | { ok: true; data: T; message?: string }
  | { ok: false; message: string };

type RazorpayPaymentFlowInput<TPrepared, TConfirmed> = {
  prepare: () => Promise<ActionResult<TPrepared>>;
  toCheckoutOptions: (prepared: TPrepared) => RazorpayCheckoutOptions;
  confirm: (args: {
    prepared: TPrepared;
    checkout: RazorpaySuccessResponse;
  }) => Promise<ActionResult<TConfirmed>>;
  successMessage?: string;
  logContext?: {
    flow: string;
    method: string;
    source: string;
    bookingId?: number | string;
  };
};

const toErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message.trim().length > 0
    ? error.message
    : fallback;

const logRazorpayFlowFailure = (
  stage: "prepare" | "open" | "confirm",
  message: string,
  context: RazorpayPaymentFlowInput<unknown, unknown>["logContext"],
  error?: unknown,
) => {
  logger.error("[payments] razorpay_flow_failure", {
    stage,
    message,
    context,
    error:
      error instanceof Error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : error,
  });
};

export async function runRazorpayPaymentFlow<TPrepared, TConfirmed>({
  prepare,
  toCheckoutOptions,
  confirm,
  successMessage,
  logContext,
}: RazorpayPaymentFlowInput<TPrepared, TConfirmed>): Promise<
  ActionResult<TConfirmed>
> {
  let prepared: ActionResult<TPrepared>;
  try {
    prepared = await prepare();
  } catch (error) {
    const message = toErrorMessage(
      error,
      "Unable to start payment. Please try again.",
    );
    logRazorpayFlowFailure("prepare", message, logContext, error);
    toast.error(message);
    return { ok: false, message };
  }

  if (!prepared.ok) {
    logRazorpayFlowFailure("prepare", prepared.message, logContext);
    toast.error(prepared.message);
    return prepared;
  }

  let checkout:
    | { ok: true; data: RazorpaySuccessResponse }
    | { ok: false; message: string };
  const nativeNavigationOpen = await beginNativePaymentNavigation();
  try {
    const options = toCheckoutOptions(prepared.data);
    const { callback_url: callbackUrl, ...handlerOptions } = options;
    const useCallback = nativeNavigationOpen && Boolean(callbackUrl);
    checkout = await openRazorpayCheckout({
      ...handlerOptions,
      ...(useCallback && callbackUrl ? { callback_url: callbackUrl } : {}),
      // Redirect mode is safe only when there is a signed return route. Keep
      // this generic helper aligned with every concrete checkout entry point so
      // a future caller cannot silently reintroduce the native browser ejection.
      redirect: useCallback,
      webview_intent: isNativePaymentShell() || options.webview_intent,
    });
  } catch (error) {
    const message = toErrorMessage(
      error,
      "Could not open payment screen. Please try again.",
    );
    logRazorpayFlowFailure("open", message, logContext, error);
    toast.error(message);
    return { ok: false, message };
  } finally {
    await endNativePaymentNavigation();
  }

  if (!checkout.ok) {
    logRazorpayFlowFailure("open", checkout.message, logContext);
    toast.error(checkout.message);
    return { ok: false, message: checkout.message };
  }

  let confirmed: ActionResult<TConfirmed>;
  try {
    confirmed = await confirm({
      prepared: prepared.data,
      checkout: checkout.data,
    });
  } catch (error) {
    const message = toErrorMessage(
      error,
      "Payment completed but confirmation failed. Please retry.",
    );
    logRazorpayFlowFailure("confirm", message, logContext, error);
    toast.error(message);
    return { ok: false, message };
  }

  if (!confirmed.ok) {
    logRazorpayFlowFailure("confirm", confirmed.message, logContext);
    toast.error(confirmed.message);
    return confirmed;
  }

  if (successMessage || confirmed.message) {
    toast.success(successMessage ?? confirmed.message ?? "Payment confirmed");
  }

  return confirmed;
}
